import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {
  useGetPopularLinksQuery,
  useGetRelatedLinksQuery,
  useGetTopHotelsQuery
} from 'modules/seoApi'
import {
  getPopularLinksParams,
  getSeoLinkParams,
  getTopHotelsParams
} from 'modules/seoApi/selectors'
import {useRouter} from 'next/router'

import type {LinksTab} from './LinksTabs'
import type {SeoLink} from 'seo/utils'

/**
 * Popular SRP links of the current place, or across all places without one
 */
export const usePopularLinksTab = (): LinksTab => {
  const {formatMessage} = useIntl()
  const params = useSelector(getPopularLinksParams)
  const {links} = useGetPopularLinksQuery(params, {
    selectFromResult: result => ({links: result.data?.links})
  })

  return {
    id: 'popular',
    label: formatMessage({
      id: 'srpLinks.popularTab',
      defaultMessage: 'Popular searches'
    }),
    links
  }
}

/**
 * SRP links related to the current SEO SRP, by place and taxonomy
 */
export const useRelatedLinksTab = (): LinksTab => {
  const {formatMessage} = useIntl()
  const {query} = useRouter()
  const params = useSelector(state => getSeoLinkParams(state, query.taxonomy))
  const {links} = useGetRelatedLinksQuery(params, {
    selectFromResult: result => ({links: result.data?.links}),
    skip: !params.placeId
  })

  return {
    id: 'related',
    label: formatMessage({
      id: 'srpLinks.relatedTab',
      defaultMessage: 'Related searches'
    }),
    links
  }
}

const useTopHotels = (
  placeId: string | undefined,
  {limit, skip = false}: {limit?: number; skip?: boolean} = {}
) => {
  const params = useSelector(state => getTopHotelsParams(state, placeId, limit))

  return useGetTopHotelsQuery(params, {
    selectFromResult: result => ({
      place: result.data?.place,
      links: result.data?.links
    }),
    skip
  })
}

/**
 * Top hotels across all places, for pages without a place of their own.
 */
export const useGlobalTopHotelsTab = (): LinksTab => {
  const {formatMessage} = useIntl()
  const {links} = useTopHotels(undefined)

  return {
    id: 'topHotels',
    label: formatMessage({
      id: 'topHotels.globalTab',
      defaultMessage: 'Top hotels'
    }),
    links
  }
}

interface PlaceTopHotelsOptions {
  // The hotel whose page renders the block
  excludedHotelId?: string
  // Links already shown in another tab of the same block
  excludedLinks?: SeoLink[]
  // Fetch more than displayed so the list stays full after the exclusions
  limit?: number
  skip?: boolean
}

const withoutExcluded = (
  links: SeoLink[] | undefined,
  {excludedHotelId, excludedLinks = []}: PlaceTopHotelsOptions
) => {
  const excludedHrefs = new Set(excludedLinks.map(link => link.href))

  return links?.filter(
    link =>
      !excludedHrefs.has(link.href) &&
      !(excludedHotelId && link.href.startsWith(`/Hotel/${excludedHotelId}/`))
  )
}

/**
 * Top hotels of one place, labelled with the name seo-api resolves it to (a
 * landmark to its city). Yields no tab while skipped or before the response
 * carries a place name.
 */
export const usePlaceTopHotelsTab = (
  placeId: string | undefined,
  options: PlaceTopHotelsOptions = {}
): LinksTab | undefined => {
  const {formatMessage} = useIntl()
  const isSkipped = Boolean(options.skip) || !placeId
  const {place, links} = useTopHotels(placeId, {
    limit: options.limit,
    skip: isSkipped
  })

  if (isSkipped || !place?.name) return undefined

  return {
    id: `topHotels-${placeId}`,
    label: formatMessage(
      {
        id: 'topHotels.placeTab',
        defaultMessage: 'Top hotels in {place}'
      },
      {place: place.name}
    ),
    links: withoutExcluded(links, options)
  }
}
